// @migration-in-progress
/**
 * Familiarisation Requirements and Management
 * WBC3 Section 12.4 and related familiarisation logic
 */

import { COMPLIANCE_CONSTANTS } from '../complianceConstants';

// Vessel Familiarisation Topics per WBC3 Appendix 8, Section 7.2, Section 14.11.3, Section 8.3.1
export const FAMILIARISATION_TOPICS = [
  {
    id: 'emergency_equipment',
    title: 'Location and Operation of Emergency Equipment',
    description: 'Fire extinguishers, life jackets, flares, EPIRB, life rafts',
    mandatory: true,
    reference: 'WBC3 Section 14.11.3'
  },
  {
    id: 'muster_evacuation',
    title: 'Muster and Evacuation Procedures',
    description: 'Assembly points, evacuation routes, abandon ship procedures',
    mandatory: true,
    reference: 'WBC3 Section 8.3.1'
  },
  {
    id: 'role_specific_duties',
    title: 'Role-Specific Emergency Duties',
    description: 'Individual responsibilities during emergencies and drills',
    mandatory: true,
    reference: 'WBC3 Appendix 8, Section 7.2 — General Familiarisation'
  },
  {
    id: 'vessel_systems',
    title: 'Vessel Systems and Controls',
    description: 'Engine controls, electrical systems, fuel systems, bilge pumps',
    mandatory: true,
    reference: 'WBC3 Appendix 8, Section 7.2 — General Familiarisation'
  },
  {
    id: 'communication',
    title: 'Communication Systems',
    description: 'VHF radio, distress procedures, internal communication',
    mandatory: true,
    reference: 'WBC3 Appendix 8, Section 7.2 — General Familiarisation'
  },
  {
    id: 'safety_procedures',
    title: 'Vessel-Specific Safety Procedures',
    description: 'Working deck procedures, machinery spaces, confined spaces',
    mandatory: true,
    reference: 'WBC3 Appendix 8, Section 7.2 — General Familiarisation'
  }
];

// Familiarisation completion rules per WBC3 Appendix 8, Section 7.2
export const FAMILIARISATION_RULES = {
  // When familiarisation is required
  triggers: [
    'First time joining vessel',
    'Returning after >6 months absence',
    'Role change or promotion',
    'After significant vessel refit',
    'After SMS policy update',
    'Master discretion'
  ],
  
  // Timing requirements
  timing: {
    before_operations: true,
    max_delay_hours: COMPLIANCE_CONSTANTS.FAMILIARISATION.COMPLETION_HOURS,
    grace_period_emergency: 'Must complete before participating in drills'
  },
  
  // Record keeping per WBC3 Appendix 8, Section 7.2
  records: {
    retention_months: 12,
    minimum_fields: ['name', 'role', 'date', 'topics_covered', 'signed_by_master'],
    export_format: ['PDF', 'HTML'],
    audit_trail: true
  },
  
  // Integration requirements
  integration: {
    drill_participation: 'Must be familiarised before drill participation (WBC3 Section 8.3.1)',
    defect_assignment: 'Can be assigned defects after familiarisation',
    equipment_checks: 'Can perform equipment checks after role familiarisation'
  }
};

/**
 * UK Workboat Code familiarisation requirements by vessel category (Enhanced)
 */
export const FAMILIARISATION_REQUIREMENTS = {
  'Cat 0': {
    requiredTraining: [
      'Vessel familiarisation',
      'Emergency procedures',
      'Fire fighting equipment',
      'Life saving equipment',
      'Navigation equipment',
      'Engine room familiarisation'
    ],
    refreshInterval: 12, // months
    mandatory: true
  },
  'Cat 1': {
    requiredTraining: [
      'Vessel familiarisation',
      'Emergency procedures', 
      'Fire fighting equipment',
      'Life saving equipment',
      'Navigation equipment'
    ],
    refreshInterval: 12,
    mandatory: true
  },
  'Cat 2': {
    requiredTraining: [
      'Vessel familiarisation',
      'Emergency procedures',
      'Fire fighting equipment', 
      'Life saving equipment'
    ],
    refreshInterval: 12,
    mandatory: true
  },
  'Cat 3': {
    requiredTraining: [
      'Vessel familiarisation',
      'Emergency procedures',
      'Life saving equipment'
    ],
    refreshInterval: 12, 
    mandatory: true
  },
  'Cat 4': {
    requiredTraining: [
      'Vessel familiarisation',
      'Emergency procedures'
    ],
    refreshInterval: 12,
    mandatory: true
  },
  'Cat 5': {
    requiredTraining: [
      'Basic vessel familiarisation'
    ],
    refreshInterval: 12,
    mandatory: true
  },
  'Cat 6': {
    requiredTraining: [
      'Basic vessel familiarisation'
    ],
    refreshInterval: 12,
    mandatory: false
  }
};

/**
 * Check if crew member needs familiarisation refresh
 * @param {Object} crewMember - Crew member object
 * @param {string} category - Vessel category (e.g., 'Cat 0')
 * @returns {boolean} True if refresh is needed
 */
export function needsFamiliarisationRefresh(crewMember, category) {
  if (!crewMember.familiarisationDate) return true;
  
  const requirements = FAMILIARISATION_REQUIREMENTS[category];
  if (!requirements) return false;
  
  const familiarisationDate = new Date(crewMember.familiarisationDate);
  const refreshDue = new Date(familiarisationDate);
  refreshDue.setMonth(refreshDue.getMonth() + requirements.refreshInterval);
  
  return new Date() > refreshDue;
}

/**
 * Get familiarisation requirements for vessel category
 * @param {string} category - Vessel category
 * @returns {Object} Requirements object
 */
export function getFamiliarisationRequirements(category) {
  return FAMILIARISATION_REQUIREMENTS[category] || FAMILIARISATION_REQUIREMENTS['Cat 6'];
}

/**
 * Generate familiarisation checklist for specific role
 * @param {string} role - Crew member role
 * @returns {Array} Array of familiarisation topics
 */
export function getFamiliarisationChecklistForRole(role) {
  let topics = [...FAMILIARISATION_TOPICS];
  
  // Add role-specific requirements
  switch (role) {
    case 'Master':
      topics.push({
        id: 'command_procedures',
        title: 'Command and Control Procedures',
        description: 'Bridge procedures, navigation equipment, command responsibilities',
        mandatory: true,
        reference: 'WBC3 Appendix 8, Section 7.2 — General Familiarisation'
      });
      break;
    case 'Engineer':
      topics.push({
        id: 'engineering_systems',
        title: 'Engineering Systems Familiarisation',
        description: 'Engine room procedures, machinery operation, emergency stops',
        mandatory: true,
        reference: 'WBC3 Appendix 8, Section 7.2 — General Familiarisation'
      });
      break;
  }
  
  return topics;
}